//core
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ActionReducerMapBuilder } from "@reduxjs/toolkit/src/mapBuilders";
import { NoInfer } from "@reduxjs/toolkit/src/tsHelpers";
import { crmBoardService } from "../../../services/admin/crmBoard.service";
import { setUser } from "../../common/user";
import { setGeneralProgressHide, setGeneralProgressShow, setGeneralSnackbarState } from "../../common/ui";

export type CrmBoardFilterType = {
  search?: string | null;
  approveStatus?: string | null;
  communicationStatuses?: string[] | null;
  trafficStatuses?: string[] | null;
  trafficSources?: string[] | null;
  trafficGeos?: string[] | null;
  managerTeams?: number[] | null;
  managerIds?: number[] | null;
  affiliateIds?: number[] | null;
  platformIds?: number[] | null;
  invitationLinkIds?: number[] | null;
  invitationManagerIds?: number[] | null;
  minBalanceAmount?: string | null;
  maxBalanceAmount?: string | null;
  hasMultiAccount?: boolean | null;
  tagIds?: number[] | null;
  subAffiliateHierarchyLevels?: number[] | null;
  subAffiliateDealIds?: number[] | null;
  isSubAffiliateProgramActive?: boolean | null;
  parentAffiliateIds?: number[] | null;
  registrationDateFrom?: string | null;
  registrationDateTo?: string | null;
};

export type GetBoardPayloadType = {
  platformId: number;
  filter?: CrmBoardFilterType;
};

export type GetColumnCardsPayloadType = {
  columnId: number;
  platformId: number;
  cursor?: string | null;
  filter?: CrmBoardFilterType;
};

export type MoveCardPayloadType = {
  cardId: number;
  platformId: number;
  targetColumnId: number;
  version: number;
  targetColumnVersion: number;
};

export type PinCardPayloadType = {
  cardId: number;
  platformId: number;
  version: number;
  isPinned: boolean;
};

export type CreateColumnPayloadType = {
  platformId: number;
  name: string;
  description: string;
};

export type UpdateColumnPayloadType = {
  columnId: number;
  platformId: number;
  name: string;
  description: string;
  version: number;
};

export type DeleteColumnPayloadType = {
  columnId: number;
  platformId: number;
  version: number;
  replacementColumnId?: number | null;
};

export type ReorderColumnsPayloadType = {
  platformId: number;
  columnIds: number[];
};

export type CrmBoardTagType = {
  id: number;
  name: string;
  color: string | null;
};

export type CrmBoardCardFieldsType = {
  username: string;
  email: string;
  managerId: number | null;
  managerUsername: string | null;
  tags: CrmBoardTagType[];
};

export type CrmBoardCardType = {
  id: number;
  affiliateId: number;
  columnId: number;
  isPinned: boolean;
  pinnedAt: string | null;
  movedAt: string;
  commentsCount: number;
  version: number;
  fields: CrmBoardCardFieldsType;
};

export type CrmBoardCardUpdateType = Partial<Omit<CrmBoardCardType, 'fields'>> & {
  id: number;
};

export type CrmBoardColumnUpdateType = {
  id: number;
  version: number;
};

export type PinCardResponseType = CrmBoardCardUpdateType;

export type MoveCardResponseType = {
  card: CrmBoardCardUpdateType;
  columns: CrmBoardColumnUpdateType[];
};

export type CrmBoardColumnType = {
  id: number;
  name: string;
  description: string | null;
  position: number;
  isDefault: boolean;
  version: number;
  cardsTotal: number;
  cards: CrmBoardCardType[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type CrmBoardDataType = {
  platformId: number;
  columns: CrmBoardColumnType[];
  hiddenColumnIds: number[];
};

export type ColumnCardsDataType = {
  cardsTotal: number;
  cards: CrmBoardCardType[];
  nextCursor: string | null;
  hasMore: boolean;
};

export type CrmBoardSliceState = {
  board: {
    data: CrmBoardDataType | null,
    error: any,
    isFetching: boolean,
  },
  columnCards: {
    data: ColumnCardsDataType | null,
    error: any,
    isFetching: boolean,
  },
  moveCard: {
    data: MoveCardResponseType | null,
    error: any,
    isFetching: boolean,
  },
  pinCard: {
    data: PinCardResponseType | null,
    error: any,
    isFetching: boolean,
  },
  createColumn: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  updateColumn: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  deleteColumn: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
  reorderColumns: {
    data: any | null,
    error: any,
    isFetching: boolean,
  },
};

const initialState: CrmBoardSliceState = {
  board: {
    data: null,
    error: null,
    isFetching: false,
  },
  columnCards: {
    data: null,
    error: null,
    isFetching: false,
  },
  moveCard: {
    data: null,
    error: null,
    isFetching: false,
  },
  pinCard: {
    data: null,
    error: null,
    isFetching: false,
  },
  createColumn: {
    data: null,
    error: null,
    isFetching: false,
  },
  updateColumn: {
    data: null,
    error: null,
    isFetching: false,
  },
  deleteColumn: {
    data: null,
    error: null,
    isFetching: false,
  },
  reorderColumns: {
    data: null,
    error: null,
    isFetching: false,
  },
};

export const getBoardData: any = createAsyncThunk(
  'crmBoard/getBoardData',
  async (payload: GetBoardPayloadType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await crmBoardService.getBoard(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: data?.error || 'common.messages.error',
        }));
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const getColumnCardsData: any = createAsyncThunk(
  'crmBoard/getColumnCardsData',
  async (payload: GetColumnCardsPayloadType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await crmBoardService.getColumnCards(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: data?.error || 'common.messages.error',
        }));
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const moveCardData: any = createAsyncThunk(
  'crmBoard/moveCardData',
  async (payload: MoveCardPayloadType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await crmBoardService.moveCard(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: data?.error || 'common.messages.error',
        }));
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const pinCardData: any = createAsyncThunk(
  'crmBoard/pinCardData',
  async (payload: PinCardPayloadType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await crmBoardService.pinCard(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: data?.error || 'common.messages.error',
        }));
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const createColumnData: any = createAsyncThunk(
  'crmBoard/createColumnData',
  async (payload: CreateColumnPayloadType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await crmBoardService.createColumn(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (response.ok && response.status === 201) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.created',
          })
        );
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: data?.error || 'common.messages.error',
        }));
        return rejectWithValue(data)
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const updateColumnData: any = createAsyncThunk(
  'crmBoard/updateColumnData',
  async (payload: UpdateColumnPayloadType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await crmBoardService.updateColumn(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: data?.error || 'common.messages.error',
        }));
        return rejectWithValue(data)
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.updated',
          })
        );
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const deleteColumnData: any = createAsyncThunk(
  'crmBoard/deleteColumnData',
  async (payload: DeleteColumnPayloadType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await crmBoardService.deleteColumn(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: data?.error || 'common.messages.error',
        }));
        return rejectWithValue(data)
      }

      if (response.ok && response.status === 200) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.removed',
          })
        );
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

export const reorderColumnsData: any = createAsyncThunk(
  'crmBoard/reorderColumnsData',
  async (payload: ReorderColumnsPayloadType, { rejectWithValue, dispatch }) => {
    dispatch(setGeneralProgressShow());
    try {
      const response = await crmBoardService.reorderColumns(payload);
      const data = await response.json();

      if (data.hasOwnProperty('authorized') && data.authorized === false) {
        dispatch(setGeneralProgressHide());
        dispatch(setUser(null));
        localStorage.removeItem('user');
      }

      if (!response.ok) {
        dispatch(setGeneralProgressHide());
        dispatch(setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: data?.error || 'common.messages.error',
        }));
        return rejectWithValue(data)
      }

      if (response.ok) {
        dispatch(
          setGeneralSnackbarState({
            open: true,
            type: 'success',
            message: 'success',
            messageKey: 'common.messages.updated',
          })
        );
      }

      dispatch(setGeneralProgressHide());
      return data;
    } catch (error) {
      dispatch(
        setGeneralSnackbarState({
          open: true,
          type: 'warning',
          message: 'error',
          messageKey: 'common.messages.error',
        })
      );
      dispatch(setGeneralProgressHide());
      return rejectWithValue(error)
    }
  }
);

const crmBoardSlice = createSlice({
  name: 'crmBoard',
  initialState: initialState,
  reducers: {
    clearBoardState(state) {
      state.board.data = null;
      state.board.error = null;
      state.board.isFetching = false;
    },
    clearColumnCardsState(state) {
      state.columnCards.data = null;
      state.columnCards.error = null;
      state.columnCards.isFetching = false;
    },
    clearMoveCardState(state) {
      state.moveCard.data = null;
      state.moveCard.error = null;
      state.moveCard.isFetching = false;
    },
    clearPinCardState(state) {
      state.pinCard.data = null;
      state.pinCard.error = null;
      state.pinCard.isFetching = false;
    },
    clearCreateColumnState(state) {
      state.createColumn.data = null;
      state.createColumn.error = null;
      state.createColumn.isFetching = false;
    },
    clearUpdateColumnState(state) {
      state.updateColumn.data = null;
      state.updateColumn.error = null;
      state.updateColumn.isFetching = false;
    },
    clearDeleteColumnState(state) {
      state.deleteColumn.data = null;
      state.deleteColumn.error = null;
      state.deleteColumn.isFetching = false;
    },
    clearReorderColumnsState(state) {
      state.reorderColumns.data = null;
      state.reorderColumns.error = null;
      state.reorderColumns.isFetching = false;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<CrmBoardSliceState>>) => {
    builder.addCase(getBoardData.pending, (state) => {
      state.board.isFetching = true;
      state.board.error = null;
    });
    builder.addCase(getBoardData.fulfilled, (state, action) => {
      state.board.error = null;
      state.board.isFetching = false;
      state.board.data = action.payload;
    });
    builder.addCase(getBoardData.rejected, (state, action) => {
      state.board.isFetching = false;
      state.board.error = action.payload;
    });

    builder.addCase(getColumnCardsData.pending, (state) => {
      state.columnCards.isFetching = true;
      state.columnCards.error = null;
    });
    builder.addCase(getColumnCardsData.fulfilled, (state, action) => {
      state.columnCards.error = null;
      state.columnCards.isFetching = false;
      state.columnCards.data = action.payload;
    });
    builder.addCase(getColumnCardsData.rejected, (state, action) => {
      state.columnCards.isFetching = false;
      state.columnCards.error = action.payload;
    });

    builder.addCase(moveCardData.pending, (state) => {
      state.moveCard.isFetching = true;
      state.moveCard.error = null;
    });
    builder.addCase(moveCardData.fulfilled, (state, action) => {
      state.moveCard.error = null;
      state.moveCard.isFetching = false;
      state.moveCard.data = action.payload;
    });
    builder.addCase(moveCardData.rejected, (state, action) => {
      state.moveCard.isFetching = false;
      state.moveCard.error = action.payload;
    });

    builder.addCase(pinCardData.pending, (state) => {
      state.pinCard.isFetching = true;
      state.pinCard.error = null;
    });
    builder.addCase(pinCardData.fulfilled, (state, action) => {
      state.pinCard.error = null;
      state.pinCard.isFetching = false;
      state.pinCard.data = action.payload;
    });
    builder.addCase(pinCardData.rejected, (state, action) => {
      state.pinCard.isFetching = false;
      state.pinCard.error = action.payload;
    });

    builder.addCase(createColumnData.pending, (state) => {
      state.createColumn.isFetching = true;
      state.createColumn.error = null;
    });
    builder.addCase(createColumnData.fulfilled, (state, action) => {
      state.createColumn.error = null;
      state.createColumn.isFetching = false;
      state.createColumn.data = action.payload;
    });
    builder.addCase(createColumnData.rejected, (state, action) => {
      state.createColumn.isFetching = false;
      state.createColumn.error = action.payload;
    });

    builder.addCase(updateColumnData.pending, (state) => {
      state.updateColumn.isFetching = true;
      state.updateColumn.error = null;
    });
    builder.addCase(updateColumnData.fulfilled, (state, action) => {
      state.updateColumn.error = null;
      state.updateColumn.isFetching = false;
      state.updateColumn.data = action.payload;
    });
    builder.addCase(updateColumnData.rejected, (state, action) => {
      state.updateColumn.isFetching = false;
      state.updateColumn.error = action.payload;
    });

    builder.addCase(deleteColumnData.pending, (state) => {
      state.deleteColumn.isFetching = true;
      state.deleteColumn.error = null;
    });
    builder.addCase(deleteColumnData.fulfilled, (state, action) => {
      state.deleteColumn.error = null;
      state.deleteColumn.isFetching = false;
      state.deleteColumn.data = action.payload;
    });
    builder.addCase(deleteColumnData.rejected, (state, action) => {
      state.deleteColumn.isFetching = false;
      state.deleteColumn.error = action.payload;
    });

    builder.addCase(reorderColumnsData.pending, (state) => {
      state.reorderColumns.isFetching = true;
      state.reorderColumns.error = null;
    });
    builder.addCase(reorderColumnsData.fulfilled, (state, action) => {
      state.reorderColumns.error = null;
      state.reorderColumns.isFetching = false;
      state.reorderColumns.data = action.payload;
    });
    builder.addCase(reorderColumnsData.rejected, (state, action) => {
      state.reorderColumns.isFetching = false;
      state.reorderColumns.error = action.payload;
    });
  },
});

export default crmBoardSlice.reducer;

export const {
  clearBoardState,
  clearColumnCardsState,
  clearMoveCardState,
  clearPinCardState,
  clearCreateColumnState,
  clearUpdateColumnState,
  clearDeleteColumnState,
  clearReorderColumnsState,
} = crmBoardSlice.actions;
